import Axios from 'axios';
import { z } from 'zod';

/**
 * `subjectType` values the activity timeline can show a reference preview
 * for. Everything else (job status, reassignment, ...) already shows what it
 * needs inline in the row's `detail` text, so those stay label-only - see
 * job-activity.presenter.ts on the API.
 */
export enum PreviewableSubjectType {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  ROOM_FEED_ITEM = 'ROOM_FEED_ITEM',
  ROOM = 'ROOM',
}

export function isPreviewableSubjectType(
  subjectType?: string | null
): subjectType is PreviewableSubjectType {
  return (
    !!subjectType &&
    (Object.values(PreviewableSubjectType) as string[]).includes(subjectType)
  );
}

const EmailActivityPreviewSchema = z.object({
  kind: z.literal('EMAIL'),
  correspondenceId: z.string(),
  direction: z.enum(['outbound', 'inbound']),
  subject: z.string().nullish(),
  from: z.string().nullish(),
  to: z.array(z.string()),
  snippet: z.string(),
  attachmentCount: z.number(),
});

const SmsActivityPreviewSchema = z.object({
  kind: z.literal('SMS'),
  smsId: z.string(),
  to: z.string().nullish(),
  recipientName: z.string().nullish(),
  message: z.string(),
});

const RoomFeedItemActivityPreviewSchema = z.object({
  kind: z.literal('ROOM_FEED_ITEM'),
  roomFeedItemId: z.string(),
  itemType: z.enum(['PHOTO_NOTE', 'INVENTORY_NOTE']),
  roomId: z.string(),
  roomName: z.string(),
  inspectionId: z.string(),
  noteText: z.string().nullish(),
  /** Photo notes only - preferred over noteText when present. */
  caption: z.string().nullish(),
  thumbnailUrl: z.string().nullish(),
  itemName: z.string().nullish(),
  brand: z.string().nullish(),
  disposition: z.string().nullish(),
});

const RoomActivityPreviewSchema = z.object({
  kind: z.literal('ROOM'),
  roomId: z.string(),
  roomName: z.string(),
  thumbnailUrl: z.string().nullish(),
});

export const ActivityPreviewSchema = z.discriminatedUnion('kind', [
  EmailActivityPreviewSchema,
  SmsActivityPreviewSchema,
  RoomActivityPreviewSchema,
  RoomFeedItemActivityPreviewSchema,
]);

export type ActivityPreview = z.infer<typeof ActivityPreviewSchema>;
export type EmailActivityPreview = z.infer<typeof EmailActivityPreviewSchema>;
export type SmsActivityPreview = z.infer<typeof SmsActivityPreviewSchema>;
export type RoomFeedItemActivityPreview = z.infer<
  typeof RoomFeedItemActivityPreviewSchema
>;
export type RoomActivityPreview = z.infer<typeof RoomActivityPreviewSchema>;

export const ActivityPreviewResponseSchema = z.object({
  /**
   * Echoed back by the API. The UI decides previewability from the activity
   * row it already has, so nothing reads this today - it is kept so the schema
   * describes the whole response rather than a subset of it.
   */
  subjectType: z.string().nullable(),
  preview: ActivityPreviewSchema.nullable(),
});

export type ActivityPreviewResponse = z.infer<
  typeof ActivityPreviewResponseSchema
>;

/** The reference an activity-history row points at, for the preview pane. */
export async function getActivityPreview({
  jobId,
  activityId,
}: {
  jobId: string;
  activityId: string;
}): Promise<ActivityPreviewResponse> {
  const res = await Axios.get(
    `/api/v2/jobs/${jobId}/activity/${activityId}/preview`
  );
  return ActivityPreviewResponseSchema.parse(res.data);
}
